import * as React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// Page d'information "Confidentialite et cookies" (RGPD / lignes directrices
// CNIL). Le site n'utilise que des traceurs strictement necessaires au
// service demande (connexion, preferences d'affichage) : ils sont exemptes
// de consentement, d'ou une simple information et pas de bandeau.
// A tenir a jour si un outil de mesure d'audience ou un service tiers
// deposant des cookies est ajoute (il faudrait alors un consentement).

export const CHEMIN_CONFIDENTIALITE = '/confidentialite';

type Traceur = { nom: string; emetteur: string; stockage: string; finalite: string; duree: string };

const TRACEURS: Traceur[] = [
  {
    nom: 'token, user',
    emetteur: 'Running Plan',
    stockage: 'sessionStorage',
    finalite: 'Jeton de connexion et profil Google (nom, e-mail, photo) pour authentifier vos requêtes.',
    duree: "Jusqu'à la déconnexion ou la fermeture de l'onglet",
  },
  {
    nom: 'moi, lastVma, allureTable, lastAllureRecord, ajustementRecup',
    emetteur: 'Running Plan',
    stockage: 'sessionStorage',
    finalite: 'Copie locale de votre profil et de vos réglages pour éviter de les recharger à chaque page.',
    duree: "Fermeture de l'onglet",
  },
  {
    nom: 'planning.*, calendrier.*',
    emetteur: 'Running Plan',
    stockage: 'sessionStorage',
    finalite: 'Mémorise le mois affiché et les filtres en cours entre deux pages.',
    duree: "Fermeture de l'onglet",
  },
  {
    nom: 'calendrier.largeurListe',
    emetteur: 'Running Plan',
    stockage: 'localStorage',
    finalite: 'Largeur de la liste du calendrier que vous avez choisie.',
    duree: "Jusqu'à effacement des données du navigateur",
  },
  {
    nom: 'Cookies de connexion Google',
    emetteur: 'Google (accounts.google.com)',
    stockage: 'Cookies du domaine Google',
    finalite: 'Fonctionnement du bouton « Se connecter avec Google ». Régis par la politique de confidentialité de Google.',
    duree: 'Définie par Google',
  },
];

const Confidentialite = ({ horsConnexion = false }: { horsConnexion?: boolean }) => (
  <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: horsConnexion ? { xs: 3, md: 6 } : 10, maxWidth: 1100, mx: 'auto' }}>
    {horsConnexion && (
      <Link href="/" underline="hover">← Retour à la connexion</Link>
    )}
    <Typography variant="h4" component="h1" sx={{ mt: horsConnexion ? 2 : 0, mb: 3 }}>
      Confidentialité et cookies
    </Typography>

    <Typography variant="h6" component="h2" gutterBottom>En bref</Typography>
    <Typography paragraph>
      Running Plan n'utilise <strong>aucun cookie publicitaire, de mesure d'audience ou de réseau social</strong>.
      Les seules informations conservées dans votre navigateur sont strictement nécessaires au fonctionnement
      du service que vous demandez (connexion et préférences d'affichage). Conformément aux règles de la CNIL,
      elles ne nécessitent pas votre consentement : cette page a pour but de vous en informer.
    </Typography>

    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>Ce qui est stocké dans votre navigateur</Typography>
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nom</TableCell>
            <TableCell>Émetteur</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Finalité</TableCell>
            <TableCell>Durée</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {TRACEURS.map((t) => (
            <TableRow key={t.nom}>
              <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>{t.nom}</TableCell>
              <TableCell>{t.emetteur}</TableCell>
              <TableCell>{t.stockage}</TableCell>
              <TableCell>{t.finalite}</TableCell>
              <TableCell>{t.duree}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Typography paragraph variant="body2" color="text.secondary">
      Le serveur de Running Plan ne dépose lui-même aucun cookie : l'authentification passe par un jeton
      envoyé avec chaque requête. Le site charge aussi une police depuis Google Fonts, ce qui transmet
      votre adresse IP à Google mais ne dépose pas de cookie.
    </Typography>

    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>Vos données d'entraînement</Typography>
    <Typography paragraph>
      Pour fournir le service, nous enregistrons sur notre serveur votre identité Google (nom, adresse e-mail),
      vos préférences (VMA, records, zones d'allure), vos séances, votre planning, votre journal et, si vous
      le configurez, l'identifiant et la clé de votre compte intervals.icu. Ces données ne sont ni vendues ni
      utilisées à des fins publicitaires. Si vous êtes suivi par un coach, celui-ci a accès à vos données
      d'entraînement.
    </Typography>
    <Typography paragraph>
      Vous pouvez consulter et modifier vos données depuis l'application, et supprimer votre compte avec
      l'ensemble de vos données depuis <em>Préférences → Zone de danger</em>.
    </Typography>

    <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>Effacer les données locales</Typography>
    <Typography paragraph>
      La déconnexion efface le jeton de connexion. Fermer l'onglet efface le reste du sessionStorage ;
      le localStorage peut être vidé depuis les réglages de votre navigateur (« Effacer les données de site »).
    </Typography>
  </Box>
);

export default Confidentialite;
